import type { Actor } from '@avatar/types';
import { ActorVariant } from '../../../../server/graphql/__generated.types';
import { ClipObject, ClipObjectPending } from '../../../context/clips';

/**
 * Returns true if the actor has one or more variant that is not archived
 */
export const actorContainsNonArchivedVariant = (actor: Actor) => {
  return actor.variants.some(variant => !variant.isArchived);
};

/**
 * Returns true if the actor contains the variant
 */
export const actorContainsActiveVariant = (
  actor: Actor,
  activeVariant: ActorVariant
) => {
  return actor.variants.some(variant => variant.id === activeVariant.id);
};

/**
 * Returns the first non-archived variant from the list
 * Undefined if such variant doesn't exist
 */
export const getFirstNonArchivedVariant = (variants: ActorVariant[]) => {
  return variants.find(variant => !variant.isArchived);
};

/**
 * Returns the first actor from the list that has one or more non-archived variants.
 * Undefined if such actor doesn't exist
 */
export const getFirstActorWithANonArchivedVariant = (actors: Actor[]) => {
  return actors.find(actor => actorContainsNonArchivedVariant(actor));
};

/**
 * Returns the first variant that has all the matching conditions with the base variant.
 * Undefined if such variant doesn't exist
 */
export const findFirstMatchingActorVariant = (inputs: {
  baseVariant: ActorVariant;
  variants: ActorVariant[];
  matchVariantId?: boolean;
  matchVersion?: boolean;
  includeArchived?: boolean;
}) => {
  const conditions: Function[] = [];

  if (inputs.matchVariantId) {
    conditions.push(
      (variant: ActorVariant) => variant.id === inputs.baseVariant.id
    );
  }

  if (inputs.matchVersion) {
    conditions.push(
      (variant: ActorVariant) => variant.version === inputs.baseVariant.version
    );
  }

  if (!inputs.includeArchived) {
    conditions.push((variant: ActorVariant) => !variant.isArchived);
  }

  return inputs.variants.find(p => conditions.every(c => c(p)));
};

export type ProjectClips = Array<ClipObject | ClipObjectPending>;

/**
 * Sorts all the user's clips by creation in order to find the first
 * project clip. Uses the first clip in the project to
 * determine the avatar and variant to use.
 */
export const getAvatarFromProjectClips = (
  projectId: string,
  clips: ProjectClips,
  avatars: Array<Actor>
) => {
  if (clips.length < 1) return;
  const clip: ClipObject | ClipObjectPending | undefined = clips
    .filter(c => c.projectId === projectId)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
  if (clip) {
    const avatar = avatars.find(a => a.id === Number(clip.actorId));
    const variant = avatar?.variants.find(
      v => v.id === Number(clip.actor.variant.id)
    );
    if (avatar && actorContainsNonArchivedVariant(avatar)) {
      let activeVariant;
      if (variant && actorContainsActiveVariant(avatar, variant)) {
        activeVariant = variant;
      }
      return { avatar, activeVariant };
    }
  }
};

/**
 * Get avatar list by project clip order with
 * most recently used avatars at the top of the list
 */
export const sortAvatarsByClipOrder = (
  projectId: string,
  avatars: Array<Actor>,
  clips: ProjectClips
) => {
  // Reduce clips to get list of actors in order of most recently used
  const avatarIdsInOrder: Array<string> | undefined = clips
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .filter(c => c.projectId === projectId)
    .reduce((acc, clip) => {
      if (acc.indexOf(`${clip.actorId}`) > -1) return acc;
      return acc.concat([`${clip.actorId}`]);
    }, [] as Array<string>);
  if (!avatarIdsInOrder) return [];
  // Add actors that have not been used
  for (let i = 0; i < avatars.length; i += 1) {
    const actor = avatars[i];
    if (avatarIdsInOrder.indexOf(`${actor.id}`) === -1)
      avatarIdsInOrder.push(`${actor.id}`);
  }
  // Map ordered list of ids to actor objects
  const actorsInOrder = avatars.slice().sort((a, b) => {
    const indexOfA = avatarIdsInOrder.indexOf(`${a.id}`);
    const indexOfB = avatarIdsInOrder.indexOf(`${b.id}`);
    if (indexOfA < indexOfB) return -1;
    if (indexOfA > indexOfB) return 1;
    return 0;
  });
  return actorsInOrder;
};
